import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { FormLabel } from "@/components/form/common/form-label";
import { cva } from "class-variance-authority";
import { twJoin, twMerge } from "tailwind-merge";
import { PopupInfo } from "@/components/common/popup-info";

const formFieldVariants = cva(["flex", "gap-2"], {
  variants: {
    type: {
      default: ["flex-col"],
      checkbox: ["items-center cursor-pointer"],
    },
  },
  defaultVariants: {
    type: "default",
  },
});

/**
 * Get the error for a form field, supporting both simple and nested field names (e.g. "email" or "address.street")
 * @param {Object} errors - The form errors object from React Hook Form
 * @param {string} fieldName - The field name, can include dots for nested fields
 * @returns {Object|undefined} The error object if found
 */
const getFieldError = (errors, fieldName) => {
  if (!errors || !fieldName) return undefined;

  // Handle nested field names (e.g. "address.street")
  const parts = fieldName.split(".");
  let error = errors;

  for (const part of parts) {
    error = error?.[part];
    if (!error) return undefined;
  }

  return error;
};

const FormField = React.forwardRef(
  (
    {
      name,
      label,
      tooltip,
      children,
      formState,
      labelAfterField,
      forId,
      type,
      className,
    },
    ref,
  ) => {
    const fieldError =
      formState?.errors && getFieldError(formState.errors, name);

    return (
      <div className={twMerge(twJoin(formFieldVariants({ type }), className))}>
        {label ? (
          <div className="flex items-center gap-1">
            <FormLabel htmlFor={name}>{label}</FormLabel>
            {tooltip ? <PopupInfo text={tooltip} /> : null}
          </div>
        ) : null}
        <Slot ref={ref} name={name} id={forId ?? name}>
          <>{children}</>
        </Slot>
        {labelAfterField ? (
          <FormLabel htmlFor={forId ?? name}>{labelAfterField}</FormLabel>
        ) : null}
        {fieldError && (
          <FormLabel className="text-destructive">
            {fieldError.message}
          </FormLabel>
        )}
      </div>
    );
  },
);
FormField.displayName = "FormField";

export { FormField };
